import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Grid,
  Headline,
  SelectablePanel,
  Type,
  Utility,
} from "mesh-component-library";
import _isEmpty from "lodash/isEmpty";
import { ContinueAsPpuModal } from "../continue-as-ppu-modal/ContinueAsPpuModal";
import { useLocale } from "../../contexts/LocaleContext";
import { useScreenBreakpoint } from "../../hooks/useScreenBreakpoint";
import {
  getCommonBenefits,
  getPartnerDetails,
  getPpuFlag,
  getServiceInfo,
  getTowCoverages,
  getServiceDetails
} from "../../selector";
import {
  clearCCardInfo,
  clearPreviousBenefits,
  commonBenefitsSuccess,
  noCommonBenefitsWithPrevious,
  updateCCardInfo,
  updateCurrentLocation,
  updateLoggedInFlag,
  updatePpuFlag,
  updateTowReason,
  updateServiceInfo
} from "../../action";
import { generatePath } from "../../utilities/RedirectUtils";
import { TRANSLATION_CONSTANTS } from "../../app-consts/translations";
import { ACCIDENT, BATTERY, CHANGE_TIRE, ENGINE_DOES_NOT_START, LOCKOUT, MECHANICAL_ELECTRIC_PROBLEM, MECHANICAL_KEY, PAYLOAD_NEW, SERVICE_CHANGE_ORIGIN, TIRE_CHANGE, TIRE_STANDARD_WITHOUT_SPARE, TOW, TRIAGE_TYPE, VANDALISM } from "../../app-consts/appConstants";
import { isABGPartner } from "../../utils";
import { convertToBoolean } from "../../utils/regexUtil";

const TowTriage = () => {
  const getTranslatedText = useLocale();
  const history = useHistory();
  const screenBreakpoint = useScreenBreakpoint();
  const dispatch = useDispatch();

  const [selectedReason, setSelectedReason] = useState("");
  const [showContinueAsPpuModal, setShowContinueAsPpuModal] = useState(false);

  const towCoverages = useSelector(getTowCoverages);
  const { displayCode = "" } = useSelector(getPartnerDetails);
  const ppuFlag = useSelector(getPpuFlag);
  const serviceInfo = useSelector(getServiceInfo);
  const commonBenefits = useSelector(getCommonBenefits);
  const serviceDetails = useSelector(getServiceDetails);

  useEffect(() => {
    const previousBenefits = commonBenefits?.previousBenefits;
    if(!!previousBenefits && previousBenefits?.benefits) {
      dispatch(updatePpuFlag("N"));
      dispatch(updateLoggedInFlag("Y"));
      dispatch(clearCCardInfo());
      dispatch(commonBenefitsSuccess(previousBenefits));
      dispatch(clearPreviousBenefits());
    }
  }, []);

  const routeToDisablementLocation = () => {
    dispatch(updateTowReason(selectedReason));
    dispatch(updateCurrentLocation({ setFromLocationServices: false }));
    history.push(generatePath("disablementlocation"));
  };

  const hasConnectedVehicle = () => {
    let benefitsVehicles = commonBenefits?.vehicles?.length > 0 ? commonBenefits.vehicles : [];
    return benefitsVehicles.some((vehicle) => convertToBoolean(vehicle.vehiclePropID));
  };

  const findServiceDetail = (text: string) =>
    serviceDetails?.serviceDetails?.find((detail) => detail.serviceText === text);

  const handleOnClickContinueToTow = () => {
    if (isABGPartner(displayCode)) {
      switch (selectedReason) {
        case "other":
          history.push(generatePath("other-triage"));
          return;

        case TIRE_STANDARD_WITHOUT_SPARE:
          localStorage.setItem(PAYLOAD_NEW, CHANGE_TIRE);
          localStorage.setItem(TRIAGE_TYPE, TIRE_CHANGE);
          localStorage.setItem(SERVICE_CHANGE_ORIGIN, TOW);
          dispatch(updateServiceInfo(findServiceDetail(TIRE_CHANGE)));
          history.replace(generatePath("triage-steps"));
          return;

        case MECHANICAL_KEY:
          localStorage.setItem(PAYLOAD_NEW, LOCKOUT);
          localStorage.setItem(TRIAGE_TYPE, LOCKOUT);
          localStorage.setItem(SERVICE_CHANGE_ORIGIN, TOW);
          dispatch(updateServiceInfo(findServiceDetail(LOCKOUT)));
          history.replace(generatePath(hasConnectedVehicle() ? "triage-steps" : "triage"));
          return;
        
        case MECHANICAL_ELECTRIC_PROBLEM:
          history.push(generatePath("mechanical-issue-triage"));
          return;

        case ENGINE_DOES_NOT_START:
          localStorage.setItem(PAYLOAD_NEW, ENGINE_DOES_NOT_START);
          localStorage.setItem(TRIAGE_TYPE, TOW);
          localStorage.setItem(SERVICE_CHANGE_ORIGIN, TOW);
          dispatch(updateServiceInfo(findServiceDetail(TOW)));
          dispatch(updateTowReason(ENGINE_DOES_NOT_START))
          history.replace(generatePath("triage-steps"));
          return;
            
        default:
          break;
      }
    }

    if (_isEmpty(towCoverages) || ppuFlag === "Y") {
      routeToDisablementLocation();
      return;
    }
    const hasCoveredListed = towCoverages.every(({ status }) => status === "covered");
    const hasNotCoveredListed = towCoverages.every(({ status }) => status === "notCovered");
    const coverageSelectedReason = towCoverages.find(({ reason }) => reason === selectedReason);

    if (hasCoveredListed) {
      if (!!coverageSelectedReason) {
        routeToDisablementLocation();
      } else {
        setShowContinueAsPpuModal(true);
      }
      return;
    }

    if (hasNotCoveredListed) {
      if (!!coverageSelectedReason) {
        setShowContinueAsPpuModal(true);
      } else {
        routeToDisablementLocation();
      }
    }
  };

  const handleOnClickContinueAsGuest = () => {
    dispatch(updatePpuFlag("Y"));
    dispatch(updateLoggedInFlag("N"));
    dispatch(updateCCardInfo({ transactionAmount: serviceInfo.originalCost }));
    dispatch(noCommonBenefitsWithPrevious({
        message: "",
        response: commonBenefits,
      })
    );
    setShowContinueAsPpuModal(false);
    routeToDisablementLocation();
  };

  const handleOnClickCancel = () => {
    setShowContinueAsPpuModal(false);
  };

  const towTriageReasons = [
    {
      id: ACCIDENT,
      reason: getTranslatedText(TRANSLATION_CONSTANTS.TOW_TRIAGE_ACCIDENT),
    },
    {
      id: VANDALISM,
      reason: getTranslatedText(TRANSLATION_CONSTANTS.TOW_TRIAGE_VANDALISM),
    },
    {
      id: TIRE_STANDARD_WITHOUT_SPARE,
      reason: getTranslatedText(TRANSLATION_CONSTANTS.TOW_TRIAGE_TIRE_WITHOUT_SPARE),
    },
    {
      id: MECHANICAL_KEY,
      reason: getTranslatedText(TRANSLATION_CONSTANTS.TOW_TRIAGE_MECHANICAL_KEY),
    },
  ];

  towTriageReasons.push({
    id: isABGPartner(displayCode) ? ENGINE_DOES_NOT_START : BATTERY,
    reason: getTranslatedText(TRANSLATION_CONSTANTS.TOW_TRIAGE_BATTERY),
  });

  if (isABGPartner(displayCode)) {
    towTriageReasons.push({
      id: MECHANICAL_ELECTRIC_PROBLEM,
      reason: getTranslatedText(TRANSLATION_CONSTANTS.TOW_TRIAGE_MECHANICAL_ISSUE),
    });
  }

  towTriageReasons.push({
    id: "other",
    reason: getTranslatedText(TRANSLATION_CONSTANTS.TOW_TRIAGE_OTHER),
  });

  return (
    <>
      <Grid verticalAlign="middle" id="tow-triage-page">
        <Grid.Column
          col={{ sm: 12, md: 4, lg: 4, xl: 4 }}
          offset={{ md: 4, lg: 4, xl: 4 }}
        >
          <Utility.Div
            utils={{
              hrLeft: "md",
              hrRight: "md",
              vrTop: "md",
              vrBottom: "md",
            }}
          >
            <Headline
              title={
                <Type
                  variant="darker"
                  align="left"
                  size={screenBreakpoint === "sm" ? "7xl" : "3xl"}
                  weight="semibold"
                >
                  {getTranslatedText(TRANSLATION_CONSTANTS.TOW_TRIAGE_TITLE)}
                </Type>
              }
            />
          </Utility.Div>
        </Grid.Column>
        <Grid.Column
          col={{ sm: 12, md: 4, lg: 4, xl: 4 }}
          offset={{ md: 4, lg: 4, xl: 4 }}
        >
          <Utility.Div
            utils={{
              hrLeft: "md",
              hrRight: "md",
            }}
          >
            <Type align="left" weight="bold">
              {getTranslatedText(TRANSLATION_CONSTANTS.TOW_TRIAGE_REASON_LABEL)}
            </Type>
          </Utility.Div>
        </Grid.Column>
        <Grid.Column
          col={{ sm: 12, md: 4, lg: 4, xl: 4 }}
          offset={{ md: 4, lg: 4, xl: 4 }}
        >
          <Utility.Div
            utils={{
              hrLeft: "md",
              hrRight: "md",
            }}
          >
            {towTriageReasons.map(({ id: reasonId, reason }, index) => (
              <SelectablePanel
                key={`tow-triage-reason-${reasonId}`}
                id={reasonId}
                checked={selectedReason === reasonId}
                hasBorder
                showInput
                text={reason}
                type="radio"
                onChange={(event) => {
                  setSelectedReason(event.currentTarget.id);
                }}
              />
            ))}
          </Utility.Div>
        </Grid.Column>
        <Grid.Column
          col={{ sm: 12, md: 4, lg: 4, xl: 4 }}
          offset={{ md: 4, lg: 4, xl: 4 }}
          utils={{
            vrTop: "md",
          }}
        >
          <Utility.Div
            utils={{
              hrLeft: "md",
              hrRight: "md",
            }}
          >
            <Button
              hasUpgrade
              shape="pill"
              size="lg"
              onClick={handleOnClickContinueToTow}
              isBlock
              disabled={_isEmpty(selectedReason)}
            >
              {getTranslatedText(
                TRANSLATION_CONSTANTS.CONTINUE_TO_TOW,
                displayCode
              )}
            </Button>
          </Utility.Div>
        </Grid.Column>
      </Grid>
      <ContinueAsPpuModal
        active={showContinueAsPpuModal}
        content={getTranslatedText(
          TRANSLATION_CONSTANTS.TOW_REASON_NOT_COVERED_MSG
        )}
        primaryButton={{
          value: getTranslatedText(TRANSLATION_CONSTANTS.CONTINUE_AS_GUEST),
          onClick: handleOnClickContinueAsGuest,
          testId: "continue-as-guest-primary-button",
        }}
        closeButton={{
          onClick: handleOnClickCancel,
          testId: "continue-as-guest-close-button",
        }}
      />
    </>
  );
};

export default TowTriage;
